/**
 * Cmarkdownxychart — design tokens
 *
 * Hand-tuned palette and metrics. Inspired by Apple Stocks / Health: a
 * restrained dark surface, generous breathing room, refined typography, a
 * single mint accent for "up" / primary, and a coral accent for "down".
 * Multi-series usage falls back to a tight harmonic palette but with reduced
 * visual weight (less saturation, lighter strokes) so charts read calmly.
 */

import type { XYChartMargins } from './xychart-types';

// Primary trend colors — used for the focused series and delta indicators.
export const XYCHART_UP_COLOR = '#66FEC1';
export const XYCHART_DOWN_COLOR = '#FF6E84';
export const XYCHART_NEUTRAL_COLOR = 'rgba(255, 255, 255, 0.55)';

// Series palette — ordered so the first 3-4 colors look strong on their own
// and additional series fall in without breaking harmony. The palette is
// tightened (one fewer hue, slightly desaturated) for the modern aesthetic.
export const XYCHART_SERIES_COLORS: readonly string[] = [
  '#66FEC1', // mint (accent)
  '#5B9EFF', // sky
  '#F29F57', // ember
  '#A78BFA', // violet
  '#FF7EB3', // rose
  '#FDCB6E', // sunlit
  '#45E0C4', // jade
  '#FF8A65', // coral
];

// Surface tokens — tuned for the dark Cabal canvas.
export const XYCHART_GRID_COLOR = 'rgba(255, 255, 255, 0.04)';
export const XYCHART_GRID_DASH = '2 6';
export const XYCHART_BASELINE_COLOR = 'rgba(255, 255, 255, 0.08)';
export const XYCHART_AXIS_COLOR = 'rgba(255, 255, 255, 0)';
export const XYCHART_TICK_LABEL_COLOR = 'rgba(255, 255, 255, 0.42)';
export const XYCHART_AXIS_TITLE_COLOR = 'rgba(255, 255, 255, 0.56)';
export const XYCHART_CATEGORY_LABEL_COLOR = 'rgba(255, 255, 255, 0.62)';

// Tooltip / popover surface
export const XYCHART_POPOVER_BACKGROUND = 'rgba(10, 14, 22, 0.82)';
export const XYCHART_POPOVER_BORDER = '1px solid rgba(255, 255, 255, 0.07)';
export const XYCHART_POPOVER_SHADOW =
  '0 22px 48px -16px rgba(0, 0, 0, 0.78), 0 2px 8px rgba(0, 0, 0, 0.5)';
export const XYCHART_POPOVER_BACKDROP = 'blur(24px) saturate(160%)';

// Crosshair
export const XYCHART_CROSSHAIR_COLOR = 'rgba(255, 255, 255, 0.18)';
export const XYCHART_CROSSHAIR_DASH = '2 5';
export const XYCHART_FOCUS_BAND_COLOR = 'rgba(255, 255, 255, 0.025)';

// Bar geometry — slightly chunkier corners with a bit more breathing room.
export const XYCHART_BAR_RADIUS = 4;
export const XYCHART_BAR_GROUP_RATIO = 0.62;
export const XYCHART_BAR_GAP = 3;

// Container constraints — the chart spreads to its parent within these bounds.
export const XYCHART_MIN_WIDTH = 320;
export const XYCHART_MIN_HEIGHT = 280;
export const XYCHART_MAX_HEIGHT = 640;
// Default expanded-mode canvas height — keeps the chart visible even when
// the surrounding flex chain hasn't yet given it an explicit height.
export const XYCHART_DEFAULT_CANVAS_HEIGHT = 420;

// Margins (in chart-pixel units). Generous left/bottom for tick + axis label.
export const XYCHART_VERTICAL_MARGINS: XYChartMargins = {
  top: 24,
  right: 28,
  bottom: 52,
  left: 56,
};
export const XYCHART_HORIZONTAL_MARGINS: XYChartMargins = {
  top: 22,
  right: 32,
  bottom: 48,
  left: 92,
};

// Zoom — multiplier on the natural per-category step. 1 = fit-to-container,
// 4 = each category 4× wider. The chart becomes horizontally scrollable above 1.
export const XYCHART_ZOOM_MIN = 1;
export const XYCHART_ZOOM_MAX = 4;
export const XYCHART_ZOOM_STEP = 0.25;
export const XYCHART_DEFAULT_ZOOM = 1;

// Animation
export const XYCHART_ENTER_DURATION_S = 0.5;
export const XYCHART_HOVER_TRANSITION_MS = 140;
export const XYCHART_DIMMED_OPACITY = 0.25;
export const XYCHART_FADED_TAG_OPACITY = 0.55;

// Compact widget
export const XYCHART_COMPACT_PADDING_TOP = 18;
export const XYCHART_COMPACT_PADDING_BOTTOM = 16;
export const XYCHART_COMPACT_PADDING_X = 18;

// Tooltip popover
export const XYCHART_POPOVER_WIDTH = 196;
export const XYCHART_POPOVER_PADDING = 12;
export const XYCHART_POPOVER_OFFSET = 14;

// Download dropdown menu
export const XYCHART_DROPDOWN_BACKGROUND = 'rgba(14, 18, 28, 0.94)';
export const XYCHART_DROPDOWN_BORDER = '1px solid rgba(255, 255, 255, 0.08)';
export const XYCHART_DROPDOWN_SHADOW =
  '0 24px 48px -12px rgba(0, 0, 0, 0.72), 0 4px 12px rgba(0, 0, 0, 0.45)';
export const XYCHART_DROPDOWN_BACKDROP = 'blur(24px) saturate(160%)';
